import { useEffect, useState } from "react";
import { TimeRange, filterByTimeRange } from "./timeRange";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface ContentRow {
  account: string;
  date: string;
  impressionsTotal: number;
  clicksTotal: number;
  reactionsTotal: number;
  commentsTotal: number;
}

const METRICS: { key: keyof Omit<ContentRow, "account" | "date">; label: string; color: string }[] = [
  { key: "impressionsTotal", label: "Impressions", color: "#008877" },
  { key: "clicksTotal", label: "Klicks", color: "#4F7CAC" },
  { key: "reactionsTotal", label: "Reaktionen", color: "#C58B3A" },
  { key: "commentsTotal", label: "Kommentare", color: "#806A9B" },
];

interface ContentMetricsChartProps {
  refreshKey: number;
  accountOptions: string[];
  timeRange: TimeRange;
}

export default function ContentMetricsChart({ refreshKey, accountOptions, timeRange }: ContentMetricsChartProps) {
  const [selectedAccount, setSelectedAccount] = useState<string>(accountOptions[0] ?? "");
  const [chartData, setChartData] = useState<ContentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set()); 

  useEffect(() => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setError(null);

    fetch(`/api/data/content?account=${encodeURIComponent(selectedAccount)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
        return res.json();
      })
      .then((rows: ContentRow[]) => {
        const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
        const filteredData = filterByTimeRange(sorted, timeRange);
        setChartData(filteredData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [selectedAccount, refreshKey, timeRange]);

  // Click on Legend Item to toggle visibility of that metric
  const handleLegendClick = (dataKey: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  return (
    <div>
      <div className="filter-button-row">
        {accountOptions.map((account) => (
          <button
            key={account}
            className={`filter-button ${selectedAccount === account ? "selected" : ""}`}
            onClick={() => setSelectedAccount(account)}
          >
            {account}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {isLoading && <p>Lade Daten...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!isLoading && !error && chartData.length === 0 && <p>Noch keine Daten für dieses Konto.</p>}

        {!isLoading && !error && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend
                onClick={(entry) => handleLegendClick(entry.dataKey as string)}
                formatter={(value, entry) => {
                  const isHidden = hiddenMetrics.has(entry.dataKey as string);
                  return (
                    <span style={{ color: isHidden ? "#aaaaaa" : "#000000", cursor: "pointer" }}>
                      {value}
                    </span>
                  );
                }}
              />
              {METRICS.map((metric) => (
                <Line
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  name={metric.label}
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={false}
                  hide={hiddenMetrics.has(metric.key)}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}